import { Globe, Pencil, Phone } from 'lucide-react';
import { InfoField } from '~/shared/InfoField';
import { formatPhoneNumber } from '~/util/functions';
import { EthnicityOptions, getPatientDemographicsOptions, RaceOptions } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { PatientDemographicsFormModal } from './forms/PatientDemographicsFormModal';
import { Button } from '~/shared/components/buttons/Button';
import type Yup from '~/util/yup';
import { verificationValidationSchema } from './forms/validation';

export function PatientDemographics({ stayId, validationSchema }: { stayId: string; validationSchema?: Yup.AnyObjectSchema }) {
	const { data: demographics } = useSuspenseQuery(getPatientDemographicsOptions(stayId));
	let ethnicitiesLabel = '';
	if (demographics.ethnicityIds?.length > 0) {
		ethnicitiesLabel = EthnicityOptions.filter(x => demographics.ethnicityIds.includes(x.id))
			.map(x => x.label)
			.join(', ');
	}

	return (
		<>
			<div className="flex justify-end gap-2">
				<Button
					Icon={Pencil}
					aria-label="Edit Demographics"
					intent="neutral"
					useSuspense
					renderModal={closeModal => (
						<PatientDemographicsFormModal
							stayId={stayId}
							validationSchema={validationSchema ?? verificationValidationSchema}
							closeModal={closeModal}
						/>
					)}
				/>
			</div>
			<div className="mb-6 grid grid-cols-1 gap-4">
				<div className="mb-3 flex items-center gap-2 text-gray-700">
					<Phone className="size-4" />
					<h3 className="font-medium">Contact Details</h3>
				</div>
				<div className="grid grid-cols-3 gap-4">
					<InfoField label="Main Phone" value={formatPhoneNumber(demographics.mainPhoneNumber)} />
					<InfoField
						label="Alternate Phone"
						value={demographics.alternatePhoneNumber ? formatPhoneNumber(demographics.alternatePhoneNumber) : null}
					/>
					<InfoField label="Email Address" value={demographics.emailAddress} />
					<InfoField label="Primary Language" value={demographics.primaryLanguageLabel} />
					<InfoField label="Needs Interpreter" value={demographics.needsInterpreterLabel} />
				</div>
			</div>
			<div className="mb-6 grid grid-cols-1 gap-4">
				<div className="mb-3 flex items-center gap-2 text-gray-700">
					<Globe className="size-4" />
					<h3 className="font-medium">Demographics</h3>
				</div>
				<div className="grid grid-cols-2 gap-4">
					<InfoField
						label="Race"
						value={
							demographics.raceIds.length > 0
								? RaceOptions.filter(x => demographics.raceIds.includes(x.id))
										.map(x => x.label)
										.join(', ')
								: null
						}
					/>
					<InfoField
						label="Ethnicity"
						value={
							demographics.ethnicityIds?.length > 0
								? EthnicityOptions.filter(x => demographics.ethnicityIds.includes(x.id))
										.map(x => x.label)
										.join(', ')
								: null
						}
					/>
				</div>
				<div className="grid grid-cols-3 gap-4">
					<InfoField label="Marital Status" value={demographics.maritalStatus} />
					<InfoField label="Religion" value={demographics.religionLabel} />
					<InfoField label="Education Level" value={demographics.educationLevelLabel} />
				</div>
			</div>
		</>
	);
}
